import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Search, X } from 'lucide-react'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import PageHeader from '../../components/PageHeader'
import TextField from '../../components/TextField'
import { useSession } from '../../lib/sessionContext'
import { navFor } from './settingsNav'
import { searchSettings } from './settingsSearch'
import { common as C, settingsStrings } from './strings'

const H = settingsStrings.hub

/* EACH PAGE SHOWS ONLY ITS OWN GROUPS (2026-10-22). The two settings pages are two entries
   in the main menu, and drawing every group on both repeated the other page's links on
   each — so a page keeps the sections it holds, and a group left with none of them is
   dropped. بيانات الإعدادات is a diagnostic table with its own menu entry, not a form — it
   is not one of the tabs. */
const TAB_GROUPS = (groups) =>
  groups
    .filter((group) => group.id !== 'system')
    .map((group) => ({ ...group, items: group.items.filter((item) => item.local) }))
    .filter((group) => group.items.length > 0)

/**
 * THE SETTINGS, AS TWO SHORT ROWS OF TABS.
 *
 * Nineteen sections would crowd any single strip, so they are never shown at once. The
 * page header carries the five GROUPS; the one card below carries the chosen group's two
 * to four SECTIONS along its top edge, over their form. Neither row is ever longer than
 * five.
 *
 * ONE COLOUR, ONE JOB. The identity colour marks whatever is CHOSEN or ACTIVE — the
 * selected group, the selected section, the panel icons, the controls. Everything at rest is
 * neutral, so the page looks the same whichever group it holds.
 *
 * One section is on screen at a time, with one Save. Moving to another section — or to a
 * group on the other settings page — with changes unsaved asks first.
 *
 * @param {'main'|'advanced'} page
 * @param {string} current  the section on screen (the URL hash)
 * @param {(id: string, focusLabel?: string) => void} onLocal  switch to one of this page's
 *   own sections
 * @param {() => Promise<boolean>} [onSave]  omit for a section with nothing to save
 * @param {import('react').ReactNode} [footer]  replaces the footer outright
 * @param {import('react').ReactNode} [extra]  a section's own action, at the far end
 * @param {{label: string}|null} [focus]  a setting to go to — a search result
 * @param {boolean} [ready]  the form has its values; a search that lands before the page's
 *   data does waits for it before looking for the field
 * @param {import('react').ReactNode} [blocked]  a refusal or failure, shown instead
 */
export default function SettingsTabs({
  page,
  title,
  current,
  onLocal,
  dirty = false,
  saving = false,
  onSave,
  onDiscard,
  footer,
  extra,
  focus = null,
  ready = true,
  blocked,
  children,
}) {
  const { session } = useSession()
  const navigate = useNavigate()
  const groups = useMemo(() => TAB_GROUPS(navFor(page, session?.roles)), [page, session?.roles])
  const group = groups.find((entry) => entry.items.some((item) => item.id === current)) ?? groups[0]
  const [query, setQuery] = useState('')
  const results = useMemo(() => searchSettings(groups, query), [groups, query])
  const [pending, setPending] = useState(null)

  /* Where a click is going. A section of this page switches the card; a section on the
     other settings page is a trip there, and lands on the section it named. */
  const go = (item, focusLabel = null) => {
    if (item.local) onLocal(item.id, focusLabel)
    else navigate(item.to, { state: { focus: focusLabel } })
  }

  const request = (item, focusLabel = null) => {
    if (item.id === current && !focusLabel) return
    if (dirty) setPending({ item, focusLabel })
    else go(item, focusLabel)
  }

  const openResult = (result) => {
    setQuery('')
    request(result.item, result.label)
  }

  return (
    <>
      <PageHeader
        title={title}
        actions={
          <div className="set-search" role="search">
            {/* Plain text, not `type="search"`: the browser draws its own clear button in a
                search field, and this one already has one. */}
            <TextField
              icon={Search}
              value={query}
              onChange={setQuery}
              placeholder={H.search}
              aria-label={H.search}
              autoComplete="off"
              onKeyDown={(event) => {
                if (event.key === 'Enter' && results[0]) openResult(results[0])
                if (event.key === 'Escape') setQuery('')
              }}
              trailing={
                query ? (
                  <button type="button" className="set-search__clear" onClick={() => setQuery('')} aria-label={H.close}>
                    <X size={14} strokeWidth={2.25} />
                  </button>
                ) : null
              }
            />
          </div>
        }
        scope={
          <div className="set-groups" role="tablist" aria-label={title}>
            {groups.map((entry) => {
              const Icon = entry.icon
              const selected = entry.id === group?.id
              return (
                <button
                  key={entry.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`set-group${selected ? ' is-selected' : ''}`}
                  onClick={() => {
                    /* A group opens on its first section — on this page if it lives here. */
                    const item = entry.items.find((candidate) => candidate.local) ?? entry.items[0]
                    if (item && !selected) request(item)
                  }}
                >
                  <span className="set-group__tile" aria-hidden="true">
                    {Icon && <Icon size={16} strokeWidth={2} />}
                  </span>
                  <span className="set-group__label">
                    <bdi>{entry.title}</bdi>
                  </span>
                </button>
              )
            })}
          </div>
        }
      />

      {blocked ??
        (query.trim() ? (
          <section className="set-results" aria-live="polite">
            {results.length === 0 ? (
              <p className="set-results__empty">
                <bdi>{H.noResults}</bdi>
              </p>
            ) : (
              <ul className="set-results__list">
                {results.map((result) => {
                  const Icon = result.item.icon
                  return (
                    <li key={`${result.item.id}:${result.label ?? ''}`}>
                      <button
                        type="button"
                        className="set-result"
                        onClick={() => openResult(result)}
                      >
                        <span className="set-result__tile" aria-hidden="true">
                          {Icon && <Icon size={15} strokeWidth={2} />}
                        </span>
                        <span className="set-result__text">
                          <span className="set-result__name">
                            <bdi>{result.label ?? result.item.label}</bdi>
                          </span>
                          <span className="set-result__path">
                            <bdi>{result.label ? `${result.group.title} · ${result.item.label}` : result.group.title}</bdi>
                          </span>
                        </span>
                        <ChevronLeft className="set-go ltr-flip" size={16} strokeWidth={2} aria-hidden="true" />
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        ) : (
          <section className="set-card">
            {group && group.items.length > 1 && (
              <div className="set-tabs" role="tablist" aria-label={group.title}>
                {group.items.map((item) => {
                  const Icon = item.icon
                  const selected = item.id === current
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      className={`set-tab${selected ? ' is-selected' : ''}`}
                      onClick={() => request(item)}
                    >
                      {Icon && <Icon className="set-tab__icon" size={16} strokeWidth={1.9} aria-hidden="true" />}
                      <bdi>{item.label}</bdi>
                    </button>
                  )
                })}
              </div>
            )}

            <Pane key={`${current}:${ready ? 'ready' : 'loading'}`} focus={focus}>
              {children}
            </Pane>

            {(footer ?? onSave) && (
              <footer className="set-card__foot">
                {footer ?? (
                  <>
                    <Button onClick={onSave} busy={saving} disabled={!dirty}>
                      {C.save}
                    </Button>
                    <Button variant="ghost" onClick={onDiscard} disabled={!dirty || saving}>
                      {C.reset}
                    </Button>
                    {/* Said beside the button it concerns, the moment it becomes true. */}
                    {dirty && (
                      <span className="set-unsaved" role="status">
                        <i aria-hidden="true" />
                        <bdi>{H.unsaved}</bdi>
                      </span>
                    )}
                    {extra && <span className="set-card__extra">{extra}</span>}
                  </>
                )}
              </footer>
            )}
          </section>
        ))}

      <ConfirmDialog
        open={Boolean(pending)}
        tone="warning"
        title={H.discardTitle}
        message={H.discardMessage}
        confirmLabel={H.discard}
        cancelLabel={H.keepEditing}
        onConfirm={() => {
          const next = pending
          setPending(null)
          onDiscard?.()
          if (next) go(next.item, next.focusLabel)
        }}
        onCancel={() => setPending(null)}
      />
    </>
  )
}

/**
 * The section's form. Re-keyed per section, so it arrives with a short fade rather than
 * swapping under the reader — and, arriving from a search result, it finds the setting by
 * the label it prints for it, brings it into view, marks it once and puts the caret in it.
 */
function Pane({ focus, children }) {
  const ref = useRef(null)

  useEffect(() => {
    if (!focus?.label) return undefined
    const timer = setTimeout(() => {
      const root = ref.current
      if (!root) return
      const label = [...root.querySelectorAll('label, .switch-field__label, .set-sub')].find((node) =>
        node.textContent.trim().startsWith(focus.label),
      )
      if (!label) return
      const block = label.closest('.set-row, .form-row, .switch-field, .set-block') ?? label
      block.scrollIntoView({ block: 'center', behavior: 'smooth' })
      block.classList.add('is-part-flash')
      setTimeout(() => block.classList.remove('is-part-flash'), 1500)
      const control = label.control ?? block.querySelector('input:not([type="hidden"]), select, textarea, button')
      control?.focus({ preventScroll: true })
    }, 120)
    return () => clearTimeout(timer)
  }, [focus])

  return (
    <div ref={ref} className="set-card__body" role="tabpanel">
      {children}
    </div>
  )
}
